import { useState } from 'react'
import {
  Crown,
  CreditCard,
  Clock,
  Sparkles,
  CheckCircle2,
  AlertTriangle,
  FileText,
  RefreshCw,
  Zap,
  Tag,
  Download,
} from 'lucide-react'
import { Badge, Button, Card, CardContent, Input } from '@/components/ui'
import { SectionHeading } from '@/components/common'
import { toast } from 'sonner'
import {
  useBillingOverview,
  useInitiateCheckoutMutation,
  useVerifySignatureMutation,
  useToggleAutoRenewMutation,
} from '../../hooks/use-billing'
import { usePublicPlans } from '@/modules/customer/hooks/use-customer'

export function SellerBillingPage() {
  const { data: overview, isLoading } = useBillingOverview()
  const { data: availablePlansData } = usePublicPlans()

  const checkoutMutation = useInitiateCheckoutMutation()
  const verifyMutation = useVerifySignatureMutation()
  const toggleAutoRenewMutation = useToggleAutoRenewMutation()

  const [couponCode, setCouponCode] = useState('')
  const [selectedPlanId, setSelectedPlanId] = useState<number | null>(null)
  const [isCheckoutModalOpen, setIsCheckoutModalOpen] = useState(false)
  const [selectedCycle, setSelectedCycle] = useState<'MONTHLY' | 'QUARTERLY' | 'YEARLY'>('MONTHLY')

  const subscription = overview?.subscription
  const currentPlan = subscription?.plan
  const invoices = overview?.invoices ?? []
  const availablePlans = (availablePlansData as any[]) ?? []

  const getPlanPrice = (plan: any) => {
    if (selectedCycle === 'YEARLY' && plan.yearlyPrice) return Number(plan.yearlyPrice)
    if (selectedCycle === 'QUARTERLY' && plan.quarterlyPrice) return Number(plan.quarterlyPrice)
    if (selectedCycle === 'MONTHLY' && plan.monthlyPrice) return Number(plan.monthlyPrice)
    return Number(plan.price)
  }

  const handleStartCheckout = async (planId: number) => {
    try {
      const checkout = await checkoutMutation.mutateAsync({ planId, couponCode })

      const isSimulated = checkout.razorpaySubscriptionId?.startsWith('sub_sim_')

      // Check if Razorpay JS SDK window object is loaded and not a mock subscription ID
      if (typeof window !== 'undefined' && (window as any).Razorpay && !isSimulated) {
        const options = {
          key: checkout.keyId || 'rzp_test_placeholder',
          subscription_id: checkout.razorpaySubscriptionId,
          name: 'Exotic Autocart Marketplace',
          description: 'Merchant Membership Activation',
          handler: async (response: any) => {
            await verifyMutation.mutateAsync({
              paymentId: response.razorpay_payment_id || `pay_sim_${Date.now()}`,
              subscriptionId: response.razorpay_subscription_id || checkout.razorpaySubscriptionId,
              signature: response.razorpay_signature || 'simulated_valid_signature',
            })
            setIsCheckoutModalOpen(false)
          },
          prefill: {
            name: (overview as any)?.user ? `${(overview as any).user.firstName} ${(overview as any).user.lastName}` : '',
            email: (overview as any)?.user?.email || '',
          },
          theme: { color: '#8b5cf6' },
        }

        const rzp = new (window as any).Razorpay(options)
        rzp.on('payment.failed', function (resp: any) {
          toast.error(resp.error?.description || 'Payment Failed.')
        })
        rzp.open()
      } else {
        // Dev / Mock test mode simulation fallback
        await verifyMutation.mutateAsync({
          paymentId: `pay_sim_${Date.now()}`,
          subscriptionId: checkout.razorpaySubscriptionId,
          signature: 'simulated_valid_signature',
        })
        setIsCheckoutModalOpen(false)
      }
    } catch (error) {
      // Handled in mutation onError
    }
  }

  const getStatusBadge = (status?: string) => {
    switch (status) {
      case 'ACTIVE':
      case 'RENEWED':
        return <Badge variant="success" className="font-extrabold uppercase text-[10px] gap-1"><CheckCircle2 className="size-3" /> Active Membership</Badge>
      case 'TRIAL':
        return <Badge variant="outline" className="font-extrabold uppercase text-[10px] gap-1 text-blue-500 border-blue-500/40 bg-blue-500/10"><Clock className="size-3" /> Free Trial Period</Badge>
      case 'GRACE_PERIOD':
        return <Badge variant="outline" className="font-extrabold uppercase text-[10px] gap-1 text-amber-500 border-amber-500/40 bg-amber-500/10"><AlertTriangle className="size-3" /> Grace Period Active</Badge>
      case 'PAST_DUE':
        return <Badge variant="destructive" className="font-extrabold uppercase text-[10px] gap-1">Payment Past Due</Badge>
      case 'SUSPENDED':
      case 'CANCELLED':
      case 'EXPIRED':
        return <Badge variant="secondary" className="font-extrabold uppercase text-[10px]">Inactive / Cancelled</Badge>
      default:
        return <Badge variant="outline" className="font-extrabold uppercase text-[10px]">{status || 'PENDING'}</Badge>
    }
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <SectionHeading
          eyebrow="Merchant Billing & Subscriptions"
          title="Billing & Subscription Center"
          description="Manage active membership tier, feature quotas, auto-renewal preferences, and invoices."
          align="left"
        />
        <Button onClick={() => setIsCheckoutModalOpen(true)} className="gap-2 font-bold bg-primary shrink-0 shadow-glow">
          <Sparkles className="size-4" /> Upgrade / Change Plan
        </Button>
      </div>

      {/* Subscription Status & Quota Cards */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Active Plan Card */}
        <Card className="lg:col-span-2 border-primary/30 bg-gradient-to-br from-primary/10 via-card to-card backdrop-blur-xl shadow-elevation-md">
          <CardContent className="p-6 space-y-6">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border/60 pb-4">
              <div className="flex items-center gap-3">
                <div className="flex size-12 items-center justify-center rounded-2xl bg-primary/20 text-primary font-bold shadow-xs">
                  <Crown className="size-7" />
                </div>
                <div>
                  <h3 className="font-display text-xl font-extrabold text-foreground">
                    {currentPlan?.name || 'No Active Plan'}
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    {currentPlan?.description || 'Subscribe to unlock car/part listing rights and lead access.'}
                  </p>
                </div>
              </div>

              {getStatusBadge(subscription?.status)}
            </div>

            <div className="grid gap-4 sm:grid-cols-3 text-xs">
              <div className="space-y-1 rounded-xl border border-border/60 bg-background/50 p-3.5">
                <span className="text-muted-foreground flex items-center gap-1 font-semibold">
                  <CreditCard className="size-3.5 text-primary" /> Billing Price
                </span>
                <p className="font-display text-lg font-extrabold text-foreground">
                  ₹{Number(currentPlan?.price ?? 0).toLocaleString()}
                  <span className="text-xs font-normal text-muted-foreground">/{currentPlan?.billingCycle?.toLowerCase() || 'month'}</span>
                </p>
              </div>

              <div className="space-y-1 rounded-xl border border-border/60 bg-background/50 p-3.5">
                <span className="text-muted-foreground flex items-center gap-1 font-semibold">
                  <Clock className="size-3.5 text-blue-500" /> Renewal Date
                </span>
                <p className="font-display text-sm font-extrabold text-foreground">
                  {subscription?.expiresAt ? new Date(subscription.expiresAt).toLocaleDateString() : 'N/A'}
                </p>
              </div>

              <div className="space-y-1 rounded-xl border border-border/60 bg-background/50 p-3.5">
                <span className="text-muted-foreground flex items-center gap-1 font-semibold">
                  <RefreshCw className="size-3.5 text-emerald-500" /> Auto Renewal
                </span>
                <div className="flex items-center justify-between pt-1">
                  <span className="font-bold text-foreground">
                    {subscription?.autoRenew ? 'Enabled' : 'Disabled'}
                  </span>
                  <input
                    type="checkbox"
                    disabled={!subscription || subscription.status !== 'ACTIVE'}
                    checked={subscription?.autoRenew ?? false}
                    onChange={(e) => {
                      if (subscription && subscription.status === 'ACTIVE') {
                        toggleAutoRenewMutation.mutate(e.target.checked)
                      }
                    }}
                    className="rounded border-border text-primary size-4 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Feature Quotas Visual Progress */}
        <Card className="border-slate-200 dark:border-border/80 bg-white dark:bg-card/80 backdrop-blur-xl shadow-md">
          <CardContent className="p-6 space-y-4">
            <span className="font-display text-base font-bold text-slate-900 dark:text-foreground flex items-center gap-2 border-b border-slate-200 dark:border-border/60 pb-3">
              <Zap className="size-5 text-amber-500" /> Active Entitlement Limits
            </span>

            <div className="space-y-3 text-xs">
              {/* Listing Limit (MAX_ACTIVE_LISTINGS or MAX_CARS + MAX_PARTS) */}
              <div className="space-y-1">
                <div className="flex justify-between font-semibold">
                  <span className="text-slate-600 dark:text-muted-foreground">Active Car/Part Listings</span>
                  <span className="text-slate-900 dark:text-foreground font-bold">
                    {(() => {
                      if (!currentPlan) return '0 (No Active Plan)'
                      const feats = currentPlan.features ?? []
                      const maxActive = feats.find((f: any) => f.featureKey === 'MAX_ACTIVE_LISTINGS')?.featureValue
                      const maxCars = feats.find((f: any) => f.featureKey === 'MAX_CARS')?.featureValue
                      const maxParts = feats.find((f: any) => f.featureKey === 'MAX_PARTS')?.featureValue

                      if (maxActive && maxActive !== 'true' && maxActive !== 'false') return `0 / ${maxActive}`
                      if (maxCars || maxParts) {
                        const total = (Number(maxCars) || 0) + (Number(maxParts) || 0)
                        return `0 / ${total > 0 ? total : 'Unlimited'}`
                      }
                      return '0 / 25'
                    })()}
                  </span>
                </div>
                <div className="h-2 w-full rounded-full bg-slate-100 dark:bg-muted overflow-hidden">
                  <div className={`h-full rounded-full ${currentPlan ? 'bg-primary w-[15%]' : 'bg-slate-300 w-0'}`} />
                </div>
              </div>

              {/* Lead Contact Credits */}
              <div className="space-y-1">
                <div className="flex justify-between font-semibold">
                  <span className="text-slate-600 dark:text-muted-foreground">Lead Contact Credits</span>
                  <span className="text-slate-900 dark:text-foreground font-bold">
                    {(() => {
                      if (!currentPlan) return '0 / 0'
                      const val = currentPlan.features?.find((f: any) => f.featureKey === 'LEAD_CREDITS')?.featureValue ?? '50'
                      return `${val} / ${val}`
                    })()}
                  </span>
                </div>
                <div className="h-2 w-full rounded-full bg-slate-100 dark:bg-muted overflow-hidden">
                  <div className={`h-full rounded-full ${currentPlan ? 'bg-emerald-500 w-[100%]' : 'bg-slate-300 w-0'}`} />
                </div>
              </div>

              {/* Boost Listing Credits */}
              <div className="space-y-1">
                <div className="flex justify-between font-semibold">
                  <span className="text-slate-600 dark:text-muted-foreground">Boost Listing Credits</span>
                  <span className="text-slate-900 dark:text-foreground font-bold">
                    {(() => {
                      if (!currentPlan) return '0 / 0'
                      const val = currentPlan.features?.find((f: any) => f.featureKey === 'BOOST_CREDITS')?.featureValue ?? '10'
                      return `${val} / ${val}`
                    })()}
                  </span>
                </div>
                <div className="h-2 w-full rounded-full bg-slate-100 dark:bg-muted overflow-hidden">
                  <div className={`h-full rounded-full ${currentPlan ? 'bg-purple-500 w-[100%]' : 'bg-slate-300 w-0'}`} />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Invoices & Payment History Table */}
      <Card className="border-border/80 bg-card/80 backdrop-blur-xl">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center justify-between border-b border-border/60 pb-3">
            <span className="font-display text-base font-bold text-foreground flex items-center gap-2">
              <FileText className="size-5 text-primary" /> Billing Invoices & Tax Breakdowns
            </span>
          </div>

          {isLoading ? (
            <p className="text-xs text-muted-foreground">Loading billing history...</p>
          ) : invoices.length > 0 ? (
            <div className="space-y-3 text-xs">
              {invoices.map((inv) => (
                <div
                  key={inv.id}
                  className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-xl border border-border/60 bg-background/50 p-4"
                >
                  <div className="flex items-center gap-3">
                    <span className="flex size-9 items-center justify-center rounded-lg bg-emerald-500/10 text-emerald-500 font-bold">
                      <FileText className="size-4" />
                    </span>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-bold text-foreground">{inv.invoiceNumber}</span>
                        <Badge variant="outline" className="text-[10px] uppercase font-bold text-emerald-500 border-emerald-500/30">
                          {inv.status}
                        </Badge>
                      </div>
                      <p className="text-[11px] text-muted-foreground mt-0.5">
                        Period: {inv.billingPeriod} • Includes 18% GST (₹{Number(inv.gstAmount).toFixed(2)})
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <span className="font-display text-base font-extrabold text-foreground">
                      ₹{Number(inv.amount).toLocaleString()}
                    </span>
                    <Button size="sm" variant="outline" className="gap-1 text-xs font-bold">
                      <Download className="size-3.5" /> PDF
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center text-xs text-muted-foreground space-y-2">
              <FileText className="mx-auto size-8 opacity-40" />
              <p>No invoices generated yet.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Plan Upgrade Modal */}
      {isCheckoutModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 overflow-y-auto">
          <div className="w-full max-w-5xl my-8 space-y-5 rounded-2xl border border-slate-200 dark:border-border/80 bg-white dark:bg-card p-6 shadow-2xl animate-in fade-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between border-b border-slate-200 dark:border-border/60 pb-3">
              <h3 className="font-display text-lg font-bold text-slate-900 dark:text-foreground flex items-center gap-2">
                <Sparkles className="size-5 text-primary" /> Select Membership Plan & Checkout
              </h3>
              <Button size="sm" variant="ghost" onClick={() => setIsCheckoutModalOpen(false)}>
                ✕
              </Button>
            </div>

            <div className="space-y-5 text-xs">
              {/* Tenure Selection Toggle Tabs */}
              <div className="flex justify-center">
                <div className="inline-flex rounded-xl bg-slate-100 dark:bg-muted p-1 border border-slate-200 dark:border-border/60 shadow-xs">
                  {(['MONTHLY', 'QUARTERLY', 'YEARLY'] as const).map((cycle) => (
                    <button
                      key={cycle}
                      type="button"
                      onClick={() => setSelectedCycle(cycle)}
                      className={`px-4 py-1.5 rounded-lg text-xs font-extrabold transition-all ${
                        selectedCycle === cycle
                          ? 'bg-primary text-primary-foreground shadow-glow'
                          : 'text-slate-600 dark:text-muted-foreground hover:text-slate-900 dark:hover:text-foreground'
                      }`}
                    >
                      {cycle === 'MONTHLY' ? 'Monthly' : cycle === 'QUARTERLY' ? 'Quarterly' : 'Yearly (Save 30%)'}
                    </button>
                  ))}
                </div>
              </div>

              {/* 4-column responsive grid layout */}
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {availablePlans.map((plan: any) => {
                  const isCurrent = currentPlan?.id === plan.id
                  const isSelected = selectedPlanId === plan.id || (isCurrent && !selectedPlanId)
                  const resolvedPrice = getPlanPrice(plan)

                  return (
                    <div
                      key={plan.id}
                      onClick={() => !isCurrent && setSelectedPlanId(plan.id)}
                      className={`p-4 rounded-xl border transition-all space-y-3 flex flex-col justify-between relative overflow-hidden ${
                        isCurrent
                          ? 'border-emerald-500/50 bg-emerald-500/10 cursor-default ring-1 ring-emerald-500/30'
                          : isSelected
                          ? 'border-primary bg-primary/10 ring-2 ring-primary/30 shadow-md cursor-pointer'
                          : 'border-slate-200 dark:border-border/80 bg-slate-50/50 dark:bg-background/50 hover:bg-slate-100 dark:hover:bg-background hover:border-slate-300 cursor-pointer'
                      }`}
                    >
                      <div>
                        <div className="flex justify-between items-start gap-1">
                          <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary font-bold">
                            <Crown className="size-4" />
                          </span>
                          {isCurrent ? (
                            <Badge variant="success" className="text-[10px] font-bold gap-1 bg-emerald-500 text-white">
                              <CheckCircle2 className="size-3" /> Current Plan
                            </Badge>
                          ) : isSelected ? (
                            <Badge variant="default" className="text-[10px] font-bold gap-1">
                              <CheckCircle2 className="size-3" /> Selected
                            </Badge>
                          ) : null}
                        </div>

                        <h4 className="font-display text-base font-bold text-slate-900 dark:text-foreground mt-2">
                          {plan.name}
                        </h4>
                        <p className="font-display text-2xl font-extrabold text-slate-900 dark:text-foreground mt-1">
                          ₹{resolvedPrice.toLocaleString()}
                          <span className="text-xs font-normal text-slate-500 dark:text-muted-foreground">
                            /{selectedCycle.toLowerCase()}
                          </span>
                        </p>
                        <p className="text-[11px] text-slate-500 dark:text-muted-foreground mt-1.5 leading-relaxed line-clamp-2">
                          {plan.description || 'Full platform seller access & inventory tools.'}
                        </p>
                      </div>

                      {/* Features & Quotas list configured by Admin */}
                      <div className="space-y-1.5 pt-3 border-t border-slate-200 dark:border-border/60 text-[11px]">
                        {Array.isArray(plan.features) && plan.features.length > 0 ? (
                          plan.features.slice(0, 5).map((feat: any, idx: number) => (
                            <div key={idx} className="flex items-center gap-1.5 text-slate-700 dark:text-foreground/90">
                              <CheckCircle2 className="size-3 text-emerald-600 dark:text-emerald-500 shrink-0" />
                              <span className="truncate">
                                <strong className="font-semibold text-slate-900 dark:text-foreground">
                                  {feat.featureKey.replace(/_/g, ' ')}:
                                </strong>{' '}
                                {String(feat.featureValue)}
                              </span>
                            </div>
                          ))
                        ) : (
                          <>
                            <div className="flex items-center gap-1.5 text-slate-700 dark:text-foreground/90">
                              <CheckCircle2 className="size-3 text-emerald-600 dark:text-emerald-500 shrink-0" />
                              <span>Vehicle & Parts Listing Access</span>
                            </div>
                            <div className="flex items-center gap-1.5 text-slate-700 dark:text-foreground/90">
                              <Zap className="size-3 text-amber-500 shrink-0" />
                              <span>Lead Contact & Inquiries</span>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>

              {/* Coupon Code Input */}
              <div className="flex items-center gap-2 rounded-xl border border-border/80 bg-background/50 p-3">
                <Tag className="size-4 text-primary shrink-0" />
                <Input
                  placeholder="Have a promo / coupon code? Enter here..."
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                  className="text-xs font-mono uppercase bg-background"
                />
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-border/60">
                <Button variant="outline" onClick={() => setIsCheckoutModalOpen(false)}>
                  Cancel
                </Button>
                <Button
                  disabled={!selectedPlanId || selectedPlanId === currentPlan?.id || checkoutMutation.isPending}
                  onClick={() => selectedPlanId && selectedPlanId !== currentPlan?.id && handleStartCheckout(selectedPlanId)}
                  className="font-bold bg-primary text-primary-foreground shadow-glow gap-1.5"
                >
                  <CreditCard className="size-4" /> {checkoutMutation.isPending ? 'Initializing...' : selectedPlanId === currentPlan?.id ? 'Current Active Plan' : 'Proceed to Razorpay Checkout'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
